/* Pureverr homepage v6 - restrained behaviour layer (no dependencies, no libraries).
   1. Analytics hooks: pushes to window.dataLayer (the store's existing GTM convention) from
      data-pv-event attributes. No new tracking and no conversion events.
   2. Reveal-on-scroll (IntersectionObserver, once; off for reduced motion and in the Theme Editor).
   3. Videos: the phone hero loads at the first idle moment on phone-width viewports only; poster first,
      silent, looping, playsinline, with a WCAG 2.2.2 pause control. Reduced motion never plays. The film
      fills its card by object-fit: nothing on the page blurs, stretches or letterboxes it.
   4. The review app's pop-up: placed under the measured header, shown at most once a session and only
      after real engagement, auto-dismissed, and a manual close remembered for a week.
   5. Proof rail: a hairline position indicator for the phone swipe row, focusable only while it scrolls.
   6. The seven-stage core: marker buttons that open, switch and close a stage card sized to its own text;
      a first-visit cue; the core picture is started and decoded ahead of any in-page jump to the section.
   7. The review app: composition fixes injected into its same-origin iframe, a carousel height that follows
      the review on show (and its "Read more"), and a position hairline.
   8. Marked phrases: a per-line stroke when enlarged text wraps a marked phrase.
   9. Disclosures: one shared open/close for every "+" drawer on the page, measured, interruptible.
   Size changes are watched by one shared ResizeObserver per document, and nothing runs a frame loop while
   nothing is moving. */
(function () {
  'use strict';
  var root = document.documentElement;
  root.classList.add('pv-js');
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobile = window.matchMedia('(max-width: 749px)');
  var design = !!(window.Shopify && window.Shopify.designMode);
  var raf = window.requestAnimationFrame ? window.requestAnimationFrame.bind(window) : function (fn) { return setTimeout(fn, 16); };
  var EASE_STD = 'cubic-bezier(0.4, 0, 0.2, 1)';
  /* decelerating from the first frame: what a drawer should do. EASE_STD eases IN as well, which is
     what made the disclosures read as sluggish-then-abrupt rather than settled. */
  var EASE_SOFT = 'cubic-bezier(0.22, 1, 0.36, 1)';
  function onChange(mq, fn) {
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn);
  }
  /* a dimensional change takes 220 to 300ms depending on how far it travels */
  function durationFor(delta) { return Math.round(Math.min(300, 220 + Math.abs(delta) * 0.6)); }
  /* one queue for anything that measures then writes, so a burst of observers costs one layout pass */
  var writeQueue = [], writeQueued = false;
  function afterLayout(fn) {
    if (writeQueue.indexOf(fn) < 0) writeQueue.push(fn);
    if (writeQueued) return;
    writeQueued = true;
    raf(function () { writeQueued = false; var q = writeQueue.slice(); writeQueue.length = 0; q.forEach(function (f) { try { f(); } catch (e) { /* one bad measurement must not stop the rest */ } }); });
  }

  /* one ResizeObserver for this document; handlers leave with their elements */
  var sizeObserver = null;
  var sizeHandlers = [];
  function watchSize(el, fn) {
    if (!('ResizeObserver' in window)) return;
    if (!sizeObserver) {
      sizeObserver = new ResizeObserver(function (entries) {
        entries.forEach(function (entry) {
          sizeHandlers.forEach(function (h) { if (h.el === entry.target) h.fn(entry); });
        });
      });
    }
    sizeHandlers = sizeHandlers.filter(function (h) {
      if (document.contains(h.el)) return true;
      sizeObserver.unobserve(h.el);
      return false;
    });
    sizeHandlers.push({ el: el, fn: fn });
    sizeObserver.observe(el);
  }

  /* ---------- 1. analytics (existing dataLayer convention only) ---------- */
  function track(target, label, extra) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: 'pv_home_interaction', pv_target: target, pv_label: (label || '').slice(0, 80) };
      if (extra) { for (var k in extra) { if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k]; } }
      window.dataLayer.push(payload);
    } catch (e) { /* analytics must never break the page */ }
  }
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-pv-event]') : null;
    if (!el) return;
    track(el.getAttribute('data-pv-event'), el.textContent.trim());
  });

  /* ---------- 2. reveal ---------- */
  var io = null;
  if (!reduce.matches && !design && 'IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -5% 0px' });
  }
  function initReveal(scope) {
    var els = scope.querySelectorAll('.pv-reveal:not(.is-in)');
    if (!els.length) return;
    if (!io) { els.forEach(function (el) { el.classList.add('is-in'); }); return; }
    els.forEach(function (el) { io.observe(el); });
    /* safety: nothing may stay hidden if the observer never fires */
    setTimeout(function () { els.forEach(function (el) { el.classList.add('is-in'); }); }, 6000);
  }

  /* ---------- 3. videos ---------- */
  function allowed(video) {
    if (reduce.matches) return false;
    if (video.hasAttribute('data-pv-mobile-only') && !mobile.matches) return false;
    if (video.hasAttribute('data-pv-desktop-only') && mobile.matches) return false;
    return true;
  }
  function load(video) {
    if (video.getAttribute('src')) return;
    var src = video.getAttribute('data-src');
    if (!src) return;
    /* Setting src is the whole load. Raising preload or calling load() as well starts a second
       pipeline for the same file (measured pulling the hero twice on this page). */
    video.setAttribute('src', src);
  }
  function controlFor(video) {
    var host = video.closest('.pv-hero');
    return host ? host.querySelector('[data-pv-motion]') : null;
  }
  function setPaused(video, paused) {
    video.dataset.pvPaused = paused ? 'true' : 'false';
    var btn = controlFor(video);
    if (btn) btn.setAttribute('aria-pressed', paused ? 'true' : 'false');
  }
  function play(video) {
    if (video.dataset.pvPaused === 'true') return;
    load(video);
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function (err) {
        /* only a refusal parks the film; an interrupted start is not a refusal */
        if (err && err.name && err.name !== 'NotAllowedError') return;
        /* autoplay refused (iOS Low Power Mode): the poster stays, and the control must appear as Play */
        var host = video.closest('.pv-hero');
        if (host) host.classList.add('has-video');
        setPaused(video, true);
      });
    }
  }

  /* ---------- THE REVEAL GATE ----------------------------------------------------------------
     The film is shown ONLY once a frame has actually been PRESENTED, never on play(), never on
     `loadedmetadata`, never on `playing`.

     Why: `playing` fires at HAVE_FUTURE_DATA, which is a promise about DATA, not about a painted
     frame. Measured on this page on 2026-09-13 with requestVideoFrameCallback counting presented
     frames: `playing` arrived at 566ms with the presented-frame count still at ZERO, and the first
     frame was presented at 573ms. On a fast connection that window is a few milliseconds; it is the
     network that decides how long it is, and nothing in the old code cared.

     Underneath the film sits the real <img> poster, server-rendered in the final geometry at the
     final crop. So while the gate is shut, the card shows the photograph - which is what must
     happen for every one of: autoplay refused, network stalled, decode not finished, element paused
     before its first frame, media error. There is no state in which the card is empty.

     rVFC is exact and is what decides wherever it exists (Chrome 83+, Safari 15.4+). Firefox has no
     rVFC, so there the gate is the defensive check below - readyState >= HAVE_CURRENT_DATA with the
     clock actually past zero and no seek in flight - polled on a bounded rAF and on `timeupdate`.
     The same check also runs where rVFC exists, so a throttled or never-firing callback cannot park
     the poster forever. */
  function framed(video) {
    if (video.dataset.pvFramed === '1') return;
    video.dataset.pvFramed = '1';
    video.classList.add('is-framed');
    var host = video.closest('.pv-hero');
    if (host) host.classList.add('has-video');
  }
  function unframe(video) {
    video.dataset.pvFramed = '';
    video.classList.remove('is-framed');
  }
  /* the defensive check: true only when the element genuinely has a current frame to paint */
  function hasFrame(video) {
    return video.readyState >= 2 && video.currentTime > 0 && !video.seeking;
  }
  function watchFrames(video) {
    if (video.dataset.pvFrameWatch === '1') return;
    video.dataset.pvFrameWatch = '1';
    if (typeof video.requestVideoFrameCallback === 'function') {
      video.requestVideoFrameCallback(function () { framed(video); });
    }
    /* bounded rAF: it exists for the engines without rVFC and as the backstop for the ones with it.
       It stops the moment a frame is confirmed, and gives up after ~20s rather than spinning. */
    var started = Date.now();
    var tick = function () {
      if (video.dataset.pvFramed === '1') return;
      if (hasFrame(video)) { framed(video); return; }
      if (Date.now() - started > 20000) { video.dataset.pvFrameWatch = ''; return; }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }
  var vio = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      var v = entry.target;
      if (!allowed(v)) { v.pause(); return; }
      if (entry.isIntersecting && entry.intersectionRatio > 0.2) play(v); else v.pause();
    });
  }, { threshold: [0, 0.2, 0.6] }) : null;
  var allVideos = [];
  function bindVideo(video) {
    if (video.dataset.pvBound) return;
    video.dataset.pvBound = '1';
    allVideos.push(video);
    var host = video.closest('.pv-hero') || video.closest('.pv-media');
    var btn = controlFor(video);
    /* `playing` only re-syncs the control and ARMS the frame watch; it never reveals the film. */
    video.addEventListener('playing', function () {
      if (video.dataset.pvPaused === 'true') setPaused(video, false);
      watchFrames(video);
    });
    video.addEventListener('loadeddata', function () { watchFrames(video); });
    video.addEventListener('timeupdate', function () { if (hasFrame(video)) framed(video); });
    /* Not removed on pause or on `waiting`: a paused or stalled element keeps painting its last
       decoded frame, and dropping back to the poster there would be a flicker, not a repair.
       `emptied` and `error` are different - after either there is no frame at all, so the poster
       comes back and the gate re-arms. */
    var lost = function (drop) {
      unframe(video);
      video.dataset.pvFrameWatch = '';
      /* the pause control controls nothing once there is no film left to pause. `emptied` is treated
         the same as `error` here: both end with no frame, and leaving the control up after one and
         not the other was an asymmetry with no reason behind it. A refusal-parked film keeps its
         control, because there the control is the only way to start it. */
      if (drop && host && video.dataset.pvPaused !== 'true') host.classList.remove('has-video');
    };
    video.addEventListener('emptied', function () { lost(true); });
    video.addEventListener('error', function () { lost(true); });
    if (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-pressed') === 'true') { setPaused(video, false); play(video); }
        else { setPaused(video, true); video.pause(); }
      });
    }
    /* Above the fold: the poster is the LCP element, so the film waits for the first idle moment rather
       than joining the initial burst (started there, the media stack was measured paying twice). */
    if (video.hasAttribute('data-pv-priority')) {
      var start = function () {
        if (video.dataset.pvStarted) return;
        video.dataset.pvStarted = '1';
        if (!allowed(video)) return;
        play(video);
        var watch = function () { video.removeEventListener('canplaythrough', watch); if (vio) vio.observe(video); };
        video.addEventListener('canplaythrough', watch);
        setTimeout(watch, 15000);
      };
      var queue = function () {
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(start, { timeout: 1500 });
        else setTimeout(start, 800);
      };
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', queue);
      else queue();
      window.addEventListener('load', function () { setTimeout(start, 30); });
      return;
    }
    if (vio) vio.observe(video); else if (allowed(video)) play(video);
  }
  function initVideos(scope) { scope.querySelectorAll('video[data-pv-video]').forEach(bindVideo); }
  var reconsider = function () {
    allVideos.forEach(function (v) {
      if (!allowed(v)) v.pause();
      else if (v.hasAttribute('data-pv-priority')) play(v);
    });
  };
  onChange(mobile, reconsider);
  onChange(reduce, reconsider);

  /* ---------- 4. the review app's pop-up ----------
     The app puts #lai-reviewPop in this document and shows it on its own schedule. The app's admin
     settings are the first place this behaviour belongs, and the theme app embed exposes none, so the
     theme gates what the app renders here instead. It is never styled through a cross-origin frame and
     no review, name, rating or photograph is touched.

     The gate, as a conservative hypothesis rather than a claim about attention: nothing on arrival; the
     earliest it can appear is 18s in AND a fifth of the page scrolled; at most one automatic appearance
     per session; gone again after 7s; a manual close is remembered for 7 days. It is also held back
     while the hero is on screen, and it sits under the header's real measured bottom, never over it. */
  var POP_SESSION = 'pvPopShown', POP_CLOSED = 'pvPopClosed', POP_DAYS = 7;
  var popGuard = null, popTimer = null, popBound = false;
  function store(kind, key, value) {
    try {
      var s = kind === 'session' ? window.sessionStorage : window.localStorage;
      if (value === undefined) return s.getItem(key);
      s.setItem(key, value);
      return value;
    } catch (e) { return null; } /* private mode, or storage refused: the page-view gate still applies */
  }
  var popShownThisView = false;
  function popSuppressed() {
    if (popShownThisView) return true;
    if (store('session', POP_SESSION)) return true;
    var closed = parseInt(store('local', POP_CLOSED) || '0', 10);
    return !!closed && (Date.now() - closed) < POP_DAYS * 86400000;
  }
  /* Under the header's real bottom - and never above where the header will be when it comes back: this
     theme's sticky header slides away on a scroll down, so its measured bottom is near zero there. The
     header's own height is the floor, so a header returning on a scroll up never finds the pop-up over it. */
  function headerFloor() {
    var h = document.querySelector('sticky-header');
    var base = headerBottom();
    if (!h) return base;
    var host = h.closest('.shopify-section') || h;
    var sticky = ['sticky', 'fixed'].indexOf(getComputedStyle(h).position) >= 0 ||
                 ['sticky', 'fixed'].indexOf(getComputedStyle(host).position) >= 0;
    if (sticky) base = Math.max(base, h.getBoundingClientRect().height);
    return base;
  }
  function placePopup() {
    var top = Math.round(headerFloor()) + (mobile.matches ? 10 : 14);
    root.style.setProperty('--pv-pop-top', top + 'px');
    root.style.setProperty('--pv-pop-x', (mobile.matches ? 12 : 20) + 'px');
  }
  /* The pop-up must never sit over the page's own work: the hero headline, any primary button, or the
     core's stage controls. Rather than silence it for whole sections, this compares rectangles - the band
     the pop-up occupies (it is position: fixed, so its box is known without it being on screen) against
     each protected element. On a phone the buttons are full width, so this fires often and correctly.
     The header is handled by the offset, and the cart drawer by its own z-index. */
  var POP_PROTECT = '.pv-hero__h1, .pv-hero__btn, .pv-shop__cta .pv__btn, .pv-guarantee__cta .pv__btn, .pv-core__stage';
  function popWouldCover() {
    var top = parseFloat(root.style.getPropertyValue('--pv-pop-top')) || 72;
    var left = parseFloat(root.style.getPropertyValue('--pv-pop-x')) || 12;
    var right = left + Math.min(300, window.innerWidth - 24);
    var bottom = top + 280;
    /* The widget is held back with visibility, which keeps its layout box, so its REAL rectangle can be
       read even while it is hidden. It is far narrower than the maximum the stylesheet allows (measured
       140px wide, 251px tall on a phone), and guarding against the maximum instead of the real box held
       the pop-up back almost everywhere on a phone, where the buttons are full width. */
    var pop = document.getElementById('lai-reviewPop');
    if (pop) {
      var pr = pop.getBoundingClientRect();
      if (pr.width > 1 && pr.height > 1) {
        left = pr.left; right = pr.right; top = pr.top; bottom = pr.bottom;
      }
    }
    var els = document.querySelectorAll(POP_PROTECT);
    for (var i = 0; i < els.length; i++) {
      var r = els[i].getBoundingClientRect();
      if (!r.width || !r.height) continue;
      if (!(r.bottom < top || r.top > bottom || r.right < left || r.left > right)) return true;
    }
    return false;
  }
  function syncCoreGuard() { root.classList.toggle('pv-pop-inway', popWouldCover()); }
  function hidePopup() {
    root.classList.remove('pv-pop-show');
    if (popTimer) { clearTimeout(popTimer); popTimer = null; }
    if (popWatch) { clearInterval(popWatch); popWatch = null; }
  }
  /* The app runs the widget on its own ~29s cycle (measured on this store: first at about 32s, then
     every 28 to 30s, about 4s each, indefinitely). The theme cannot move that schedule from here, so it
     opens a single window instead: once the gate passes, the next appearance the app makes is allowed
     through, held for 7s, and then the window closes for the rest of the session. If the app does not
     show anything within 90s the window simply closes, and nothing has appeared. */
  var popWatch = null;
  function popIsVisible() {
    var pop = document.getElementById('lai-reviewPop');
    if (!pop) return false;
    var cs = getComputedStyle(pop);
    return cs.display !== 'none' && parseFloat(cs.opacity) > 0.05 && pop.getBoundingClientRect().height > 0;
  }
  function showPopup() {
    if (popSuppressed() || popWatch) return;
    if (!document.getElementById('lai-reviewPop')) return;
    placePopup();
    root.classList.add('pv-pop-show');
    var waited = 0;
    popWatch = setInterval(function () {
      waited += 500;
      if (popIsVisible() && !root.classList.contains('pv-pop-inway') && !root.classList.contains('pv-hero-inview')) {
        clearInterval(popWatch); popWatch = null;
        popShownThisView = true;
        store('session', POP_SESSION, '1');
        placePopup();
        popTimer = setTimeout(function () { root.classList.remove('pv-pop-show'); popTimer = null; }, 7000);
        return;
      }
      if (waited >= 90000) { clearInterval(popWatch); popWatch = null; root.classList.remove('pv-pop-show'); }
    }, 500);
  }
  function initPopupGuard(scope) {
    if (!('IntersectionObserver' in window)) return;
    /* Held back while the hero is on screen: its headline and its primary button live there, and it is
       the arrival state. The seven-stage core is handled separately, by the rectangle test below, because
       holding the pop-up back for the whole core section would silence it across most of the page. The
       cart drawer needs no guard of its own: it is z-index 1000 against the pop-up's 99, so it already
       paints over it - and an open drawer or modal sets body.overflow-hidden, which the CSS also holds on. */
    var hero = scope.querySelector ? scope.querySelector('.pv-hero') : null;
    if (hero) {
      if (popGuard) popGuard.disconnect();
      root.classList.add('pv-hero-inview');
      popGuard = new IntersectionObserver(function (entries) {
        var en = entries[entries.length - 1];
        root.classList.toggle('pv-hero-inview', en.isIntersecting && en.intersectionRatio >= 0.12);
      }, { threshold: [0, 0.12, 0.5] });
      popGuard.observe(hero);
    }
    if (popBound) return;
    popBound = true;
    placePopup();
    window.addEventListener('resize', function () { afterLayout(function () { placePopup(); syncCoreGuard(); }); }, { passive: true });

    var arrived = Date.now();
    function scrolledEnough() {
      var d = document.documentElement;
      var max = Math.max(1, (document.body.scrollHeight || d.scrollHeight) - window.innerHeight);
      return (window.scrollY || d.scrollTop || 0) / max >= 0.2;
    }
    var check = function () {
      if (popSuppressed()) { window.removeEventListener('scroll', onScroll); return; }
      if (Date.now() - arrived < 18000) return;
      if (!scrolledEnough()) return;
      window.removeEventListener('scroll', onScroll);
      showPopup();
    };
    /* Two separate scroll listeners on purpose. The gate's listener retires once the window has opened;
       the "would it cover something" guard must keep running for the whole page view, because the pop-up
       can appear at any point in the app's own cycle. Folding them into one listener froze the guard at
       whatever it read the moment the gate fired, and the pop-up was then held back for good. */
    var guardQueued = false;
    function onGuardScroll() {
      if (guardQueued) return;
      guardQueued = true;
      raf(function () { guardQueued = false; syncCoreGuard(); });
    }
    syncCoreGuard();
    window.addEventListener('scroll', onGuardScroll, { passive: true });

    var queued = false;
    function onScroll() { if (queued) return; queued = true; raf(function () { queued = false; check(); }); }
    window.addEventListener('scroll', onScroll, { passive: true });
    setTimeout(check, 18200);

    /* the app's own close control: remember it, and stop the auto-dismiss timer */
    document.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('#lai-reviewPop .scm-close-popup, #lai-reviewPop .close') : null;
      if (!t) return;
      store('local', POP_CLOSED, String(Date.now()));
      hidePopup();
    }, true);
  }

  /* ---------- 5. proof rail ---------- */
  function initRail(scope) {
    scope.querySelectorAll('[data-pv-rail]').forEach(function (rail) {
      if (rail.dataset.pvBound) return;
      var sc = rail.querySelector('[data-pv-rail-scroller]');
      if (!sc) return;
      rail.dataset.pvBound = '1';
      var bar = rail.querySelector('.pv-rail__progress');
      var thumb = bar ? bar.querySelector('.pv-rail__thumb') : null;
      var queued = false;
      function update() {
        queued = false;
        var max = sc.scrollWidth - sc.clientWidth;
        var over = max > 2;
        /* a scrollable region must be reachable by keyboard, but only while it actually scrolls */
        if (over) sc.setAttribute('tabindex', '0'); else sc.removeAttribute('tabindex');
        if (!bar || !thumb) return;
        bar.hidden = !over;
        if (!over) return;
        var trackW = bar.clientWidth;
        var thumbW = Math.max(12, trackW * (sc.clientWidth / sc.scrollWidth));
        var frac = Math.min(1, Math.max(0, sc.scrollLeft / max));
        thumb.style.width = thumbW + 'px';
        thumb.style.transform = 'translateX(' + ((trackW - thumbW) * frac).toFixed(1) + 'px)';
      }
      function queue() { if (queued) return; queued = true; raf(update); }
      sc.addEventListener('scroll', queue, { passive: true });
      window.addEventListener('resize', queue, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(queue);
      update();
    });
  }

  /* ---------- 6. the seven-stage core ---------- */
  function headerBottom() {
    var h = document.querySelector('sticky-header');
    if (!h) return 0;
    var host = h.closest('.shopify-section') || h;
    var pos = getComputedStyle(host).position;
    var own = getComputedStyle(h).position;
    if (pos !== 'sticky' && pos !== 'fixed' && own !== 'sticky' && own !== 'fixed') return 0;
    var r = h.getBoundingClientRect();
    return r.bottom > 0 ? r.bottom : 0;
  }

  /* One set of document listeners for every core on the page, added once: the Theme Editor re-renders
     sections, and per-section document listeners would otherwise pile up for the life of the page. */
  var coreWarmers = [];
  var coreIntentBound = false;
  function coreIntent(e) {
    coreWarmers = coreWarmers.filter(function (w) { return document.contains(w.core); });
    var pending = coreWarmers.filter(function (w) { return !w.isHot(); });
    if (!pending.length) return;
    var a = e.target && e.target.closest ? e.target.closest('a[href*="#"]') : null;
    if (!a) return;
    pending.forEach(function (w) { if (w.linksHere(a)) w.warm('high'); });
  }
  function coreHash() {
    coreWarmers.forEach(function (w) { if (document.contains(w.core) && w.hashHere()) w.warm('high'); });
  }
  function bindCoreIntent() {
    if (coreIntentBound) return;
    coreIntentBound = true;
    ['pointerdown', 'touchstart', 'mouseover', 'focusin', 'click'].forEach(function (type) {
      document.addEventListener(type, coreIntent, { passive: true, capture: true });
    });
    window.addEventListener('hashchange', coreHash);
  }

  function initCore(scope) {
    scope.querySelectorAll('[data-pv-core]').forEach(function (core) {
      if (core.dataset.pvBound) return;
      core.dataset.pvBound = '1';
      var stage = core.querySelector('[data-pv-core-stage]');
      var fig = core.querySelector('.pv-core__figure');
      var img = core.querySelector('.pv-core__img');
      var card = core.querySelector('.pv-core__card');
      var closeBtn = core.querySelector('[data-pv-core-close]');
      var dots = [].slice.call(core.querySelectorAll('[data-pv-stage]'));

      /* --- the picture: never a blank figure ---
         Reveal it whole once decoded (over its blurred placeholder). Start it early: when the section
         comes within one and a half screens, and at once, at high priority, on any intent to follow an
         in-page link to this section. */
      function ready() { if (fig) fig.classList.add('is-ready'); }
      /* reveal once decoded; a decode that never settles cannot keep a loaded picture hidden */
      function decodeThenReady() {
        var settled = false;
        var finish = function () { if (!settled) { settled = true; ready(); } };
        setTimeout(finish, 1500);
        if (img && typeof img.decode === 'function') img.decode().then(finish, finish);
        else finish();
      }
      if (img) {
        if (img.complete && img.naturalWidth) decodeThenReady();
        else {
          img.addEventListener('load', decodeThenReady, { once: true });
          img.addEventListener('error', ready, { once: true });
        }
      } else {
        ready();
      }
      var warmed = '';
      function warm(priority) {
        if (!img || warmed === 'high' || (warmed && priority !== 'high')) return;
        if (priority === 'high' && !(img.complete && img.naturalWidth)) img.setAttribute('fetchpriority', 'high');
        warmed = priority;
        /* the lazy-load resumption steps start the request as soon as loading becomes eager */
        if (img.getAttribute('loading') !== 'eager') img.setAttribute('loading', 'eager');
      }
      function linksHere(a) {
        var href = a.getAttribute('href') || '';
        var i = href.indexOf('#');
        if (i < 0) return false;
        if (i > 0) {
          try {
            var u = new URL(href, window.location.href);
            if (u.host !== window.location.host || u.pathname !== window.location.pathname) return false;
          } catch (e) { return false; }
        }
        var id = decodeURIComponent(href.slice(i + 1));
        if (!id) return false;
        if (id === core.id) return true;
        var t = document.getElementById(id);
        return !!(t && core.contains(t));
      }
      function hashHere() {
        try { return !!window.location.hash && decodeURIComponent(window.location.hash.slice(1)) === core.id; }
        catch (e) { return false; }
      }
      coreWarmers.push({ core: core, linksHere: linksHere, warm: warm, hashHere: hashHere, isHot: function () { return warmed === 'high'; } });
      bindCoreIntent();
      if (hashHere() || design) warm('high');

      /* The first-use signifier stays put until a stage is actually selected, by pointer or by keyboard. */
      function touch() { core.classList.add('is-touched'); }
      if ('IntersectionObserver' in window) {
        var near = new IntersectionObserver(function (entries) {
          if (entries.some(function (en) { return en.isIntersecting; })) { warm('low'); near.disconnect(); }
        }, { rootMargin: '150% 0px 150% 0px' });
        near.observe(core);
        var onScreenTimer = null;
        var seen = new IntersectionObserver(function (entries) {
          entries.forEach(function (en) {
            core.classList.toggle('is-onscreen', en.isIntersecting);
            /* the selection ripple is script-driven, so the CSS play-state rules cannot pause it */
            (ripples || []).forEach(function (a) {
              if (en.isIntersecting) { if (a.playState === 'paused') a.play(); }
              else if (a.playState === 'running') a.pause();
            });
            /* a safety net for a missed load event only: never reveal a picture that is still arriving,
               the blurred placeholder is the better state on a slow network */
            if (en.isIntersecting && !onScreenTimer) {
              onScreenTimer = setTimeout(function () { if (img && img.complete && img.naturalWidth) decodeThenReady(); }, 4000);
            }
          });
        }, { threshold: [0, 0.3] });
        seen.observe(core);
      } else {
        warm('low');
        core.classList.add('is-onscreen');
      }

      if (!stage || !dots.length) return;
      var panels = dots.map(function (dot) { return document.getElementById(dot.getAttribute('aria-controls')); });
      var current = -1;
      var shown = -1;
      var cardAnim = null;
      core.classList.add('is-enhanced');

      function focusEl(el) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } }

      /* the figure and the whole card in view, without the visitor hunting for it */
      function keepInView() {
        var r = stage.getBoundingClientRect();
        var c = card ? card.getBoundingClientRect() : r;
        var top = Math.min(r.top, c.top);
        var bottom = Math.max(r.bottom, c.bottom);
        var vh = window.innerHeight || document.documentElement.clientHeight;
        var ceiling = headerBottom() + 12;
        var delta = 0;
        if (bottom - top > vh - ceiling - 12) delta = top - ceiling;
        else if (top < ceiling) delta = top - ceiling;
        else if (bottom > vh - 12) delta = bottom - (vh - 12);
        if (Math.abs(delta) < 4) return;
        try { window.scrollBy({ top: delta, behavior: reduce.matches ? 'auto' : 'smooth' }); }
        catch (e) { window.scrollBy(0, delta); }
      }

      /* the stage row is as tall as the core or the card, whichever is taller */
      function syncCardHeight(h) {
        if (!card) return;
        var v = Math.ceil(h || card.getBoundingClientRect().height);
        if (stage.style.getPropertyValue('--pv-card-h') !== v + 'px') stage.style.setProperty('--pv-card-h', v + 'px');
      }

      /* Show one stage in the card. Switching while open: the outgoing text leaves the flow where it
         stands and fades, the incoming text fades up, and the card eases from its old height to the new
         one before handing back to its natural (auto) height. */
      function showPanel(i, animate) {
        if (i === shown || i < 0) return;
        var from = shown;
        shown = i;
        var canAnimate = animate && from >= 0 && !reduce.matches && card && typeof card.animate === 'function';
        var h0 = canAnimate ? card.getBoundingClientRect().height : 0;
        var out = from >= 0 ? panels[from] : null;
        if (out && canAnimate) {
          out.style.top = out.offsetTop + 'px';
          out.style.left = out.offsetLeft + 'px';
          out.style.width = out.offsetWidth + 'px';
          out.classList.add('is-leaving');
          setTimeout(function () {
            out.classList.remove('is-leaving');
            out.style.top = out.style.left = out.style.width = '';
          }, 180);
        }
        panels.forEach(function (panel, n) {
          if (!panel) return;
          panel.classList.remove('is-entering');
          panel.classList.toggle('is-active', n === i);
        });
        var incoming = panels[i];
        if (canAnimate && incoming) { void incoming.offsetWidth; incoming.classList.add('is-entering'); }
        if (!canAnimate) { syncCardHeight(); return; }
        var h1 = card.getBoundingClientRect().height;
        if (cardAnim) cardAnim.cancel();
        if (Math.abs(h1 - h0) >= 2) {
          card.style.overflow = 'hidden';
          cardAnim = card.animate([{ height: h0 + 'px' }, { height: h1 + 'px' }], { duration: durationFor(h1 - h0), easing: EASE_STD });
          var done = function () { card.style.overflow = ''; cardAnim = null; };
          cardAnim.onfinish = done;
          cardAnim.oncancel = done;
        }
        syncCardHeight(h1);
      }

      /* THE SELECTION RIPPLE, driven from here since 2026-09-14. It used to be a CSS animation keyed to
         aria-expanded, plus an `is-familiar` class added 7.6s after the FIRST selection that removed the
         animation rule for the rest of the page view - so every stage chosen after those 7.6s got no
         ripple at all (measured: selections at 0.4s, 2.4s and 4.5s rippled; at 9.0s and 10.5s nothing).
         Now each change of selected stage cancels every ring this core owns and starts the new marker's
         rings from frame zero; closing the stage cancels them. The rings, keyframes and timing are the
         ones the CSS had: two hairline rings 1.1s apart, 3.2s each, twice, then still. Script-driven
         animations do not obey CSS play-state rules, so the on-screen observer pauses and resumes them.
         Reduced motion: none (the rings are also display:none there). */
      var ripples = [];
      var RIPPLE_GROW = [{ transform: 'scale(0.24)' }, { transform: 'scale(1)' }];
      var RIPPLE_FADE = [{ opacity: 0, offset: 0 }, { opacity: 0.22, offset: 0.14 }, { opacity: 0.07, offset: 0.62 }, { opacity: 0, offset: 1 }];
      function stopRipple() {
        ripples.forEach(function (a) { a.cancel(); });
        ripples = [];
      }
      function startRipple(i) {
        stopRipple();
        if (i < 0 || reduce.matches || !dots[i] || !dots[i].parentNode) return;
        var rings = dots[i].parentNode.querySelectorAll('.pv-core__ring');
        if (!rings.length || typeof rings[0].animate !== 'function') return;
        [].forEach.call(rings, function (ring, k) {
          /* fill backwards: the second ring holds its hidden first frame through its 1.1s delay */
          ripples.push(ring.animate(RIPPLE_GROW, { duration: 3200, iterations: 2, delay: k * 1100, fill: 'backwards', easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }));
          ripples.push(ring.animate(RIPPLE_FADE, { duration: 3200, iterations: 2, delay: k * 1100, fill: 'backwards', easing: 'linear' }));
        });
        if (!core.classList.contains('is-onscreen')) ripples.forEach(function (a) { a.pause(); });
      }
      function setState(i) {
        var changed = i !== current;
        current = i;
        dots.forEach(function (dot, n) { dot.setAttribute('aria-expanded', n === i ? 'true' : 'false'); });
        stage.setAttribute('data-state', i < 0 ? 'idle' : 'open');
        if (changed) startRipple(i);
      }
      function open(i, how) {
        if (i < 0 || i >= dots.length) return;
        how = how || {};
        touch();
        var fromIdle = current < 0;
        showPanel(i, !fromIdle);
        setState(i);
        if (how.focus) focusEl(dots[i]);
        if (how.user) setTimeout(keepInView, fromIdle && !reduce.matches ? 160 : 0);
      }
      /* the card fades out still holding its last stage, so it never collapses while it leaves */
      function close(how) {
        how = how || {};
        var was = current;
        if (was < 0) return;
        setState(-1);
        if (how.focus) focusEl(dots[was]);
      }

      dots.forEach(function (dot, i) {
        /* the first-use label leaves when a stage is actually SELECTED. Bare focus is not a selection:
           a keyboard visitor tabbing past Stage 01 used to lose the affordance without having chosen
           anything. Arrow keys and Enter both route through open(), which calls touch() itself. */
        dot.addEventListener('pointerdown', touch, { passive: true });
        dot.addEventListener('click', function () {
          touch();
          if (current === i) { close(); return; }
          open(i, { user: true });
          track('core_stage', dot.getAttribute('data-pv-stage-name') || String(i + 1), { pv_stage: i + 1 });
        });
        dot.addEventListener('keydown', function (e) {
          var k = e.key, to = -1;
          if (k === 'Escape') { if (current >= 0) { e.preventDefault(); close({ focus: true }); } return; }
          if (k === 'ArrowDown' || k === 'ArrowRight') to = (i + 1) % dots.length;
          else if (k === 'ArrowUp' || k === 'ArrowLeft') to = (i - 1 + dots.length) % dots.length;
          else if (k === 'Home') to = 0;
          else if (k === 'End') to = dots.length - 1;
          if (to < 0) return;
          e.preventDefault();
          open(to, { user: true, focus: true });
        });
      });
      if (closeBtn) closeBtn.addEventListener('click', function () { close({ focus: true }); });
      core.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && current >= 0 && !e.defaultPrevented) close({ focus: true });
      });
      if (card) watchSize(card, function () { if (!cardAnim) syncCardHeight(); });

      /* Very large text leaves no readable room for the card beside the core on a phone: work out the
         card's would-be width in ems and stack it under the core when it is too narrow. */
      var textEl = core.querySelector('.pv-core__text') || core.querySelector('.pv-core__name');
      function fit() {
        if (!mobile.matches) { core.classList.remove('is-stacked'); return; }
        var rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
        var vw = document.documentElement.clientWidth || window.innerWidth;
        var coreW = fig ? fig.offsetWidth : 0;
        var room = vw - (coreW * 0.6 + rootPx * 0.6 + rootPx * 2.8) - rootPx * 1.6;
        var fs = textEl ? parseFloat(getComputedStyle(textEl).fontSize) : rootPx * 1.45;
        core.classList.toggle('is-stacked', room / fs < 12);
      }
      fit();
      var fitQueued = false;
      /* detaches itself once the Theme Editor has replaced this section */
      function onFitResize() {
        if (!document.contains(core)) { window.removeEventListener('resize', onFitResize); return; }
        if (fitQueued) return;
        fitQueued = true;
        raf(function () { fitQueued = false; fit(); });
      }
      window.addEventListener('resize', onFitResize, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
      onChange(mobile, function () { if (document.contains(core)) fit(); });

      /* Theme Editor: selecting a stage block shows that stage */
      core.addEventListener('shopify:block:select', function (e) {
        var panel = e.target && e.target.closest ? e.target.closest('[data-pv-panel]') : null;
        var n = panels.indexOf(panel);
        if (n >= 0) open(n);
      });
    });
  }

  /* ---------- 7. the review card carousel ---------- */
  /* The app renders a slick carousel inside a srcdoc iframe (same-origin), so the theme can give the
     card real gutters on phones and restyle the arrows without touching any app setting, review or
     review copy. Wrapped in try/catch throughout: a failure leaves the app exactly as it shipped.
     Measured box chain on phones: body offset 15px a side, slide margins 15px a side. The row is sized so
     the card is 74% of the screen and never wider than 296px, with 18px of row padding: each small chevron
     sits centred in its gutter inside a 44px invisible target, centred on the card, clear of the card. */
  var REVIEW_CSS = [
    'body .lai-title{display:none !important;}',
    'body .scm-row > svg.slick-arrow{box-sizing:content-box !important;width:16px !important;height:16px !important;padding:14px !important;margin:0 !important;',
    '  background:transparent !important;border:0 !important;border-radius:0 !important;box-shadow:none !important;cursor:pointer;}',
    'body .scm-row > svg.slick-arrow path{stroke:#1c3f60 !important;stroke-width:4.5 !important;}',
    'body .scm-row > svg.slick-arrow.slick-disabled{opacity:.35 !important;}',
    /* every card is as tall as its own review, not the longest one */
    'body .slick-track{display:flex !important;align-items:flex-start !important;}',
    'body .slick-slide{height:auto !important;}',
    'body .slick-slide > div, body .slick-slide .item{height:auto !important;min-height:0 !important;}',
    /* the app reserves a fixed 90px text box in every card; a short review should not carry that gap */
    'body .item .content-read-more, body .item .content-full{min-height:0 !important;height:auto !important;}',
    /* "Read more" opens the whole review in place (the app caps it in a 166px scroll box) */
    'body .item.show-full .content-full{max-height:none !important;overflow:visible !important;}',
    'body .slick-list{transition:none !important;}',
    /* THE TAIL UNDER THE CARDS. The app appends a 50px empty `.last-element` spacer below the carousel
       and gives its `.grid` another 15px of bottom margin; the two stacked with this section's own
       bottom padding and left about 103px of nothing under the last review on a phone. The spacer is
       kept in the DOM (something of the app's may still be watching it) and only its box is removed.
       Nothing else about the app is touched, and this is scoped to the homepage module's frame. */
    'body .last-element{display:block !important;height:0 !important;min-height:0 !important;margin:0 !important;padding:0 !important;overflow:hidden !important;}',
    'body .grid{margin-bottom:0 !important;}',
    /* PUREVERR BLUE. Owner ruling 2026-09-12: the homepage stars are the brand navy #1c3f60, which is
       what this line carried before a previous pass changed it to the product page's gold. Reverted.
       `:not(.svg_none)` is load-bearing, not tidiness, and is NOT reverted with the colour. The app's
       EMPTY star is `svg.Path_1_.svg_none`, so it carries `Path_1_` too and a bare `.group-star svg`
       rule paints it as well, overriding the app's own fill. Left unguarded, a 3-star review renders as
       FIVE filled stars - measured on the "3 out of 5" card (slide 2) by an independent reader. The rule
       this replaces had exactly that shape and painted empty stars solid navy, so the misrepresentation
       is older than the colour change and must not come back with it.
       The empty star is then given the same 0.2 navy ghost the product rating widget uses, so an empty
       star is unmistakably empty and still part of the same blue treatment.
       The `.lai-star-rating` half carried no guard until an independent reader pointed out that the
       comment above it claimed every rule here was guarded. It is latent - that widget does not appear
       inside this iframe today - but it is the exact rule shape that produced the five-filled-stars bug,
       so it is guarded rather than trusted. */
    'body .group-star svg:not(.svg_none), body .group-star svg:not(.svg_none) path,',
    'body .lai-star-rating svg:not(.svg_none), body .lai-star-rating svg:not(.svg_none) path{fill:#1c3f60 !important;}',
    'body .group-star svg.svg_none, body .group-star svg.svg_none path{fill:rgba(28,63,96,.2) !important;}',
    'body .group-star svg{width:14px !important;height:14px !important;}',
    'body .group-star{gap:3px !important;}',
    '@media (max-width: 749px){',
    '  body .grid{padding-left:0 !important;padding-right:0 !important;padding-bottom:0 !important;}',
    '  body .scm-container, body .scm-reviews-importer{padding-left:0 !important;padding-right:0 !important;}',
    '  body .scm-row{position:relative !important;box-sizing:border-box !important;width:calc(min(74vw, 296px) + 66px) !important;max-width:calc(100vw - 30px) !important;margin:0 auto !important;padding:0 18px !important;}',
    '  body .scm-row > svg.slick-arrow{position:absolute !important;top:calc(50% - 22px) !important;z-index:5 !important;}',
    '  body .scm-row > svg.slick-arrow-left{left:-13px !important;right:auto !important;}',
    '  body .scm-row > svg.slick-arrow-right{right:-13px !important;left:auto !important;}',
    '  body .item{padding-top:0 !important;}',
    '}'
  ].join('\n');

  function tuneReviewFrame(frame, holder) {
    try {
      if (frame.dataset.pvTuned) return true;
      var doc = frame.contentDocument;
      if (!doc || !doc.body) return false;
      var list = doc.querySelector('.slick-list');
      if (!list) return false;
      frame.dataset.pvTuned = '1';
      var style = doc.createElement('style');
      style.setAttribute('data-pv-review-style', '');
      style.textContent = REVIEW_CSS;
      (doc.head || doc.body).appendChild(style);
      var win = frame.contentWindow;
      /* slick sizes its slides in JS from the list width: tell it the box changed */
      if (win) {
        var kick = function () { try { win.dispatchEvent(new win.Event('resize')); } catch (e) { /* older engines */ } };
        kick();
        setTimeout(kick, 120);
        setTimeout(kick, 600);
      }
      bindReviewHeight(frame, holder);
      return true;
    } catch (e) { return false; }
  }

  /* The carousel's height follows the review(s) on show, and a hairline under the cards shows the
     position.

     ROOT CAUSE this replaces (measured 2026-09-12, reproduced on warm cache, cold cache, 4x CPU, delayed
     images, both phone and desktop): the hairline used to be positioned by ACCUMULATING a negative
     margin-top (`current + delta`) so it would sit inside the app's own empty band. margin-top on an
     element that is in the flow moves everything after it too. The first measurement runs while the app's
     iframe is still at its initial oversized height, so the delta is huge: -352px on a phone and -499px
     on desktop in ordinary warm loads, -1418px with images delayed. The whole page below the hairline
     was pulled up by that amount and the next section painted over the review cards. A moment later the
     app settled, the routine ran again and corrected to about -30px, which is why a refresh "fixed" it.

     The fix is structural, not a z-index: the hairline is out of the flow (absolute, inside a reserved
     band on .pv-reviews__app) and its position is computed absolutely rather than accumulated, and then
     clamped into that band. A wrong measurement can now only misplace a 2px rule by a few pixels inside
     space that is already reserved; it can never move another section. On top of that the height is
     re-measured when the app mutates its own DOM and when an image inside a card finishes loading, and
     every measurement and write goes through one rAF queue. */
  function bindReviewHeight(frame, holder) {
    var app = holder && holder.parentNode ? holder.parentNode : null;
    var bar = app ? app.querySelector('.pv-reviews__progress') : null;
    var thumb = bar ? bar.querySelector('span') : null;
    try {
      var doc = frame.contentDocument, win = frame.contentWindow;
      var row = doc.querySelector('.scm-row');
      var list = doc.querySelector('.slick-list');
      if (!row || !list) return;
      var $ = win.jQuery;
      var anim = null, frameAnim = null, writing = false;

      /* §15 the review never changes under the reader: the app ships this carousel on a 2s autoplay. */
      function stopAutoplay() {
        if (!$) return;
        try {
          var sl = $(row).slick('getSlick');
          if (sl && sl.options && sl.options.autoplay) $(row).slick('slickSetOption', 'autoplay', false, false);
          $(row).slick('slickPause');
        } catch (e) { /* not initialised yet; the next settle call tries again */ }
      }

      function perView() {
        try { return Math.max(1, parseInt($(row).slick('slickGetOption', 'slidesToShow'), 10) || 1); } catch (e) { return 1; }
      }
      function itemsFor(first) {
        if (first == null) return [].slice.call(doc.querySelectorAll('.slick-slide.slick-active .item'));
        var out = [];
        for (var n = 0; n < perView(); n++) {
          var el = doc.querySelector('.slick-slide[data-slick-index="' + (first + n) + '"] .item');
          if (el) out.push(el);
        }
        return out;
      }
      function targetHeight(first) {
        var h = 0;
        itemsFor(first).forEach(function (it) { h = Math.max(h, it.getBoundingClientRect().height); });
        return Math.ceil(h);
      }
      /* the app's own content under the carousel, measured from the document rather than from the
         iframe's height, which the app may still be settling */
      function spaceBelow() {
        var listBottom = list.getBoundingClientRect().bottom;
        var bottom = listBottom;
        [].forEach.call(doc.body.children, function (el) {
          if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return;
          var r = el.getBoundingClientRect();
          if (r.height > 0 && r.bottom > bottom) bottom = r.bottom;
        });
        return Math.min(160, Math.max(0, Math.round(bottom - listBottom)));
      }

      var appBox = holder && holder.closest ? holder.closest('.pv-reviews') : null;
      /* The app leaves its own empty space under the last card (measured about 69px here). Left alone it
         stacks on top of the section's bottom padding and the reviews section ends with roughly twice the
         page's rhythm. The measured tail is published as a variable and the section's own bottom padding
         absorbs it, clamped, with a floor - it changes nothing but this section's own padding. */
      function publishTail() {
        if (!appBox) return;
        var t = Math.max(0, Math.min(96, spaceBelow() - 24));
        var next = Math.round(t) + 'px';
        if (appBox.style.getPropertyValue('--pv-app-tail') !== next) appBox.style.setProperty('--pv-app-tail', next);
      }

      function setHeight(first) {
        var to = targetHeight(first);
        if (!to) return;
        var from = list.getBoundingClientRect().height;
        var frameFrom = frame.getBoundingClientRect().height;
        var frameTo = Math.round(list.getBoundingClientRect().top + to + spaceBelow());
        /* THE WRAPPER IS HANDED THE SAME NUMBER AS THE IFRAME, never zero. Before a real card has been
           measured the wrapper holds a fixed reservation open so nothing below it jumps; after, it must
           still be at least as tall as the iframe it contains, because the iframe is NOT what gives the
           wrapper its height - the app's own container is, and that container lags. Released to nothing,
           it measured 142px around a 399px iframe on a warm load and the review card painted 195px over
           the next section: the same class of failure as the old accumulating negative margin, from the
           opposite direction. While a shrink is in flight the taller of the two is held, so the iframe is
           never wider than its box mid-animation either. 160px is a floor against releasing the pre-load
           reservation on a half-built card. */
        function holdFrame(px) { if (holder) holder.style.setProperty('--pv-app-measured', Math.round(px) + 'px'); }
        if (holder && to >= 160) { holder.classList.add('is-measured'); holdFrame(Math.max(frameFrom, frameTo)); }
        writing = true;
        list.style.height = to + 'px';
        /* a floor as well as a height: if a picture inside a card decodes late and the card grows, the
           carousel is never left shorter than what it is showing */
        list.style.minHeight = to + 'px';
        frame.style.minHeight = frameTo + 'px';
        if (anim) { anim.cancel(); anim = null; }
        if (frameAnim) { frameAnim.cancel(); frameAnim = null; }
        if (Math.abs(to - from) >= 2 && !reduce.matches && typeof list.animate === 'function') {
          var timing = { duration: durationFor(to - from), easing: EASE_STD };
          anim = list.animate([{ height: from + 'px' }, { height: to + 'px' }], timing);
          if (typeof frame.animate === 'function') frameAnim = frame.animate([{ minHeight: frameFrom + 'px' }, { minHeight: frameTo + 'px' }], timing);
          anim.onfinish = function () { anim = null; placeBar(); };
          if (frameAnim) frameAnim.onfinish = function () { frameAnim = null; holdFrame(frameTo); };
          else holdFrame(frameTo);
        } else {
          holdFrame(frameTo);
        }
        placeBar();
        publishTail();
        raf(function () { writing = false; });
      }

      /* The hairline's position, computed from scratch every time and clamped into the reserved band.
         Out of the flow, so nothing else on the page can be moved by it. */
      function placeBar() {
        if (!bar || bar.hidden || !app) return;
        try {
          var appRect = app.getBoundingClientRect();
          var cardsBottom = frame.getBoundingClientRect().top + list.getBoundingClientRect().bottom - appRect.top;
          var lo = cardsBottom + 8;
          var hi = Math.max(lo, appRect.height - 4);
          var top = Math.max(lo, Math.min(hi, cardsBottom + 13));
          var next = Math.round(top) + 'px';
          if (bar.style.getPropertyValue('--pv-bar-top') !== next) bar.style.setProperty('--pv-bar-top', next);
        } catch (e) { /* the stylesheet's own default keeps it inside the same reserved band */ }
      }

      function updateBar() {
        if (!bar || !thumb) return;
        try {
          var real = doc.querySelectorAll('.slick-slide:not(.slick-cloned)').length;
          var visible = doc.querySelectorAll('.slick-slide.slick-active').length || 1;
          var cur = doc.querySelector('.slick-current');
          var idx = cur ? parseInt(cur.getAttribute('data-slick-index'), 10) : NaN;
          if (!(real > visible) || isNaN(idx)) { bar.hidden = true; return; }
          idx = ((idx % real) + real) % real;
          bar.hidden = false;
          var trackW = bar.clientWidth;
          var thumbW = Math.max(12, trackW * Math.min(1, visible / real));
          var steps = Math.max(1, real - 1);
          thumb.style.width = thumbW + 'px';
          thumb.style.transform = 'translateX(' + ((trackW - thumbW) * Math.min(1, idx / steps)).toFixed(1) + 'px)';
          placeBar();
        } catch (e) { bar.hidden = true; }
      }

      var pendingFirst = null;
      function schedule(first) {
        if (first != null) pendingFirst = first;
        afterLayout(function () { var f = pendingFirst; pendingFirst = null; setHeight(f); updateBar(); });
      }

      /* every card's own size, re-observed whenever the app rebuilds its slides */
      var itemWatch = win.ResizeObserver ? new win.ResizeObserver(function () { if (!anim && !writing) schedule(); }) : null;
      function observeItems() {
        if (!itemWatch) return;
        try { itemWatch.disconnect(); } catch (e) { /* already gone */ }
        doc.querySelectorAll('.slick-slide .item').forEach(function (it) { itemWatch.observe(it); });
      }
      observeItems();

      /* a picture inside a card finishing late is the other way a measurement goes stale */
      doc.addEventListener('load', function (e) {
        if (e.target && e.target.tagName === 'IMG') schedule();
      }, true);

      /* the app may replace its own markup (and with it our injected stylesheet) at any point */
      var moTimer = null;
      if (win.MutationObserver && doc.body) {
        new win.MutationObserver(function () {
          if (moTimer) return;
          moTimer = setTimeout(function () {
            moTimer = null;
            if (!doc.querySelector('style[data-pv-review-style]')) {
              try {
                var st = doc.createElement('style');
                st.setAttribute('data-pv-review-style', '');
                st.textContent = REVIEW_CSS;
                (doc.head || doc.body).appendChild(st);
              } catch (e) { /* the app's own styling stands */ }
            }
            stopAutoplay();
            observeItems();
            schedule();
          }, 140);
        }).observe(doc.body, { childList: true, subtree: true });
      }

      watchSize(frame, function () { afterLayout(placeBar); });
      if (holder) watchSize(holder, function () { afterLayout(placeBar); });

      if ($) {
        $(row).on('beforeChange', function (e, slick, currentSlide, nextSlide) { schedule(nextSlide); });
        $(row).on('afterChange setPosition', function () { stopAutoplay(); observeItems(); schedule(); });
      }
      /* a short settling ladder rather than one guess, and it stops: no permanent polling */
      [120, 400, 900, 1800, 3200].forEach(function (ms) { setTimeout(function () { stopAutoplay(); schedule(); }, ms); });
    } catch (e) { if (bar) bar.hidden = true; }
  }

  function initReviews(scope) {
    scope.querySelectorAll('.pv-reviews__app-frame').forEach(function (holder) {
      if (holder.dataset.pvWatching) return;
      holder.dataset.pvWatching = '1';
      var tries = 0;
      var tick = function () {
        tries += 1;
        var frame = holder.querySelector('iframe.scm-reviews-importer-iframe, iframe');
        var done = frame ? tuneReviewFrame(frame, holder) : false;
        if (!done && tries < 40) setTimeout(tick, 400);
      };
      tick();
      /* if the app swaps its iframe for a new one, start again on the new one (and only then) */
      if ('MutationObserver' in window) {
        new MutationObserver(function () {
          var frame = holder.querySelector('iframe');
          if (frame && !frame.dataset.pvTuned) { tries = 0; tick(); }
        }).observe(holder, { childList: true });
      }
      window.addEventListener('resize', function () {
        var frame = holder.querySelector('iframe');
        if (frame && frame.dataset.pvTuned && frame.contentWindow) {
          try { frame.contentWindow.dispatchEvent(new frame.contentWindow.Event('resize')); } catch (e) { /* no-op */ }
        }
      }, { passive: true });
    });
  }

  /* ---------- the rating count beside the app's stars ---------- */
  /* Read from the app's own data attribute, never typed in: if it is missing or unreadable, nothing is written. */
  function initRating(scope) {
    scope.querySelectorAll('.pv-shop__appstars').forEach(function (holder) {
      if (holder.dataset.pvWatching) return;
      holder.dataset.pvWatching = '1';
      var tries = 0;
      var tick = function () {
        tries += 1;
        var node = holder.querySelector('[data-rate-version2]');
        if (node && !holder.querySelector('.pv-shop__count')) {
          try {
            var data = JSON.parse(node.getAttribute('data-rate-version2'));
            var total = parseInt(data && data.total, 10);
            if (total > 0) {
              var href = holder.getAttribute('data-pv-reviews-url');
              var span = document.createElement('span');
              span.className = 'pv-shop__count';
              var label = total + (total === 1 ? ' review' : ' reviews');
              if (href) {
                var a = document.createElement('a');
                a.href = href;
                a.textContent = label;
                a.setAttribute('data-pv-event', 'featured_product_reviews');
                span.appendChild(a);
              } else { span.textContent = label; }
              holder.appendChild(span);
              return;
            }
          } catch (e) { return; }
        }
        if (tries < 40) setTimeout(tick, 400);
      };
      tick();
    });
  }

  /* ---------- 7b. disclosures ----------
     One behaviour for every "+" drawer: the real content height is measured and eased open, and the
     close runs the same way before <details> actually closes, so the content never vanishes under the
     summary. The element stays a native <details>/<summary>, so keyboard, focus and assistive technology
     work whether or not this runs; aria-expanded is kept in step for the same reason. Reduced motion and
     engines without the Web Animations API get the browser's own instant behaviour. */
  function initDisclosure(scope) {
    scope.querySelectorAll('.pv__details').forEach(function (d) {
      if (d.dataset.pvBound) return;
      d.dataset.pvBound = '1';
      var sum = d.querySelector('summary');
      var body = d.querySelector('.pv__details-body');
      if (!sum || !body) return;
      var inner = body.querySelector('.pv__details-inner') || body;
      var anim = null, closing = false, turn = 0;
      function sync() { sum.setAttribute('aria-expanded', (d.open && !closing) ? 'true' : 'false'); }
      sync();
      d.addEventListener('toggle', sync);
      sum.addEventListener('click', function (e) {
        if (reduce.matches || typeof body.animate !== 'function') return;
        e.preventDefault();
        /* Both directions are interruptible. While a close is running <details> is still open, so asking
           "is it open?" would send a second click straight back into the close branch and the drawer would
           never come back. The direction in flight is what a click reverses, so that is what is tracked. */
        /* THE HEIGHT A CLOSED DRAWER REPORTS IS NOT ZERO. Chrome hides the non-summary content of a
           closed <details> with content-visibility rather than display:none, and a measurement taken
           there comes back as a full, plausible, viewport-dependent height (measured 2026-09-12: 265.6px
           at 390 and 526.3px at 1440 on a drawer that had never been opened, against a true open height
           of 266 and 182). Fed into the opening keyframes that produced `from 265.562px to 266px` - an
           animation that runs for 260ms and moves nothing, which is precisely the "opens too immediately"
           this pass was asked to fix; at 1440 it was worse, animating 526 DOWN to 182, a collapse where
           an expansion belonged. So the start height is taken from the state, not from the box: a closed
           drawer starts at 0, and only an open one (including one still animating, which keeps d.open
           true in both directions) is measured. scrollHeight, read after the element is open, is sound
           and is still what the target comes from. */
        var from = d.open ? body.getBoundingClientRect().height : 0;
        if (anim) { anim.cancel(); anim = null; }
        /* Every click takes a turn. A superseded animation's finish handler checks its turn against the
           current one and does nothing if it is stale - cancel() alone is not enough to rely on, because a
           cancelled animation's finish handler was still landing here and closing a drawer that the third
           of three rapid clicks had just reopened. */
        var mine = ++turn;
        var opening = closing || !d.open;
        closing = false;
        body.style.height = '';
        body.style.overflow = 'hidden';
        if (opening) {
          d.open = true;
          sync();
          var to = body.scrollHeight;
          anim = body.animate([{ height: from + 'px', opacity: from > 1 ? 1 : 0 }, { height: to + 'px', opacity: 1 }], { duration: 260, easing: EASE_SOFT });
          if (inner !== body && typeof inner.animate === 'function') inner.animate([{ transform: 'translateY(6px)' }, { transform: 'none' }], { duration: 300, easing: EASE_SOFT });
          var done = function () {
            if (mine !== turn) return;
            body.style.height = ''; body.style.overflow = ''; anim = null;
          };
          anim.onfinish = done; anim.oncancel = done;
        } else {
          closing = true;
          sync();
          anim = body.animate([{ height: from + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 220, easing: EASE_SOFT });
          anim.onfinish = function () {
            if (mine !== turn) return;
            closing = false; d.open = false; sync();
            body.style.height = ''; body.style.overflow = ''; anim = null;
          };
          anim.oncancel = function () {
            if (mine !== turn) return;
            body.style.height = ''; body.style.overflow = ''; anim = null;
          };
        }
      });
    });
  }

  /* ---------- 8. marked phrases ---------- */
  var marks = [];
  function checkMarks() {
    marks.forEach(function (m) {
      m.classList.remove('is-wrapped');
      var cs = getComputedStyle(m);
      var lh = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.4;
      if (m.getBoundingClientRect().height > lh * 1.5) m.classList.add('is-wrapped');
    });
  }
  var marksQueued = false;
  function initMarks(scope) {
    var found = [].slice.call(scope.querySelectorAll('.pv__mark')).filter(function (m) { return marks.indexOf(m) < 0; });
    if (!found.length) return;
    var first = marks.length === 0;
    marks = marks.filter(function (m) { return document.contains(m); }).concat(found);
    checkMarks();
    if (first) {
      window.addEventListener('resize', function () {
        if (marksQueued) return;
        marksQueued = true;
        raf(function () { marksQueued = false; checkMarks(); });
      }, { passive: true });
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(checkMarks);
    }
  }

  /* ---------- boot, and re-boot per section in the Theme Editor ---------- */
  function init(scope) {
    initReveal(scope); initVideos(scope); initPopupGuard(scope); initRail(scope); initCore(scope);
    initReviews(scope); initRating(scope); initMarks(scope); initDisclosure(scope);
  }
  init(document);
  document.addEventListener('shopify:section:load', function (e) { init(e.target || document); });
  document.addEventListener('shopify:section:reorder', function (e) { init(e.target || document); });
  document.addEventListener('shopify:block:select', function (e) {
    var s = e.target && e.target.closest ? e.target.closest('.shopify-section') : null;
    init(s || document);
  });
})();
